/**
 * Google Identity Services 연동.
 *
 * 구글이 발급한 **ID 토큰**만 받아 서버로 넘긴다. 서버가 구글 공개키로 검증하고
 * 우리 토큰을 내준다 — 클라이언트는 구글 토큰을 저장하지 않는다.
 *
 * OAuth 리디렉션 방식을 쓰지 않는 이유: 페이지를 떠났다 돌아와야 해서 비로그인
 * 상태의 LocalStorage 목록과 화면 상태를 다시 맞춰야 한다. ID 토큰 방식은
 * 페이지를 벗어나지 않아 "고르던 중에 로그인" 흐름이 끊기지 않는다.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface CredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_black' | 'filled_blue';
      size?: 'small' | 'medium' | 'large';
      shape?: 'rectangular' | 'pill';
      text?: 'signin_with' | 'continue_with';
      width?: number;
      locale?: string;
    },
  ) => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

let loading: Promise<GoogleAccountsId> | null = null;

/**
 * GIS 스크립트를 한 번만 불러온다.
 *
 * 실패를 캐시하지 않는다 — 네트워크가 잠깐 끊겼을 때 새로고침 전까지 영영
 * 로그인을 못 하게 되면 안 된다.
 */
export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (loading !== null) {
    return loading;
  }

  loading = new Promise<GoogleAccountsId>((resolve, reject) => {
    const existing = window.google?.accounts?.id;
    if (existing !== undefined) {
      resolve(existing);
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const api = window.google?.accounts?.id;
      if (api === undefined) {
        reject(new Error('Google Identity Services를 불러오지 못했습니다'));
        return;
      }
      resolve(api);
    };
    script.onerror = () => reject(new Error('Google Identity Services 스크립트 로드 실패'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = null;
    throw error;
  });

  return loading;
}

/**
 * `initialize`는 **클라이언트 ID당 한 번만** 부른다.
 *
 * GIS는 전역 싱글톤이라 다시 부르면 앞의 설정을 덮어쓴다. 예전에는 화면이
 * 다시 그려질 때마다(언어 전환, 부모 리렌더) 불러서 콘솔에 "initialize() is
 * called multiple times" 경고가 쌓였고, 누르는 순간 버튼이 갈아끼워져 클릭이
 * 사라지기도 했다.
 *
 * 콜백은 여기 담아 두고 초기화 때 만든 함수가 **읽기만** 한다. 그래야 콜백이
 * 매번 달라져도 초기화를 다시 할 이유가 없다.
 */
let initializedFor: string | null = null;
let handlers: Pick<GoogleButtonOptions, 'onCredential' | 'onError'> | null = null;

async function ensureInitialized(clientId: string): Promise<GoogleAccountsId> {
  const api = await loadGoogleIdentity();
  if (initializedFor === clientId) {
    return api;
  }
  api.initialize({
    client_id: clientId,
    callback: (response) => {
      const credential = response.credential;
      if (credential === undefined || credential === '') {
        handlers?.onError(new Error('구글이 토큰을 주지 않았습니다'));
        return;
      }
      handlers?.onCredential(credential);
    },
    // 자동 로그인을 끈다 — 로그아웃한 사용자가 화면을 열자마자 다시
    // 로그인되면 "로그아웃이 안 된다"고 느낀다.
    auto_select: false,
    cancel_on_tap_outside: true,
  });
  initializedFor = clientId;
  return api;
}

export interface GoogleButtonOptions {
  clientId: string;
  container: HTMLElement;
  locale: string;
  onCredential: (idToken: string) => void;
  onError: (error: unknown) => void;
}

/**
 * 구글이 그리는 공식 버튼을 붙인다.
 *
 * 직접 버튼을 만들지 않는 이유: 구글 브랜드 가이드가 로고·문구·비율을 규정하고
 * 있고, 직접 만들면 규정을 어기기 쉽다. 사용자도 이 버튼 모양에 익숙하다.
 *
 * ⚠️ **모양을 우리가 다 정할 수는 없다.** 버튼은 구글이 그리는 iframe이라
 * 글꼴·높이(40px)·안쪽 여백은 저쪽 것이다. 맞출 수 있는 건 모서리와 폭뿐이라
 * 거기까지만 맞춘다 — 우리 버튼과 같은 각진 사각형, 같은 모서리 반경.
 *
 * 이것보다 더 맞추려면 ID 토큰 방식을 버리고 인증 코드 방식으로 바꿔야
 * 한다(서버가 코드를 토큰으로 바꾸고, 클라이언트 시크릿을 들고 있어야 한다).
 * 지금은 그만한 값은 아니라고 보고 여기서 멈춘다.
 */
export function renderGoogleButton({
  clientId,
  container,
  locale,
  onCredential,
  onError,
}: GoogleButtonOptions): void {
  handlers = { onCredential, onError };

  ensureInitialized(clientId)
    .then((api) => {
      container.replaceChildren();
      api.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        // 우리 버튼은 각진 사각형이다. 알약으로 두면 이 버튼만 딴 세상 물건이 된다
        shape: 'rectangular',
        text: 'continue_with',
        locale,
        width: Math.min(container.offsetWidth || 320, 400),
      });
    })
    .catch(onError);
}

/** 로그아웃할 때 부른다. 다음 로그인에서 계정 선택 화면이 다시 뜬다. */
export function disableGoogleAutoSelect(): void {
  window.google?.accounts?.id?.disableAutoSelect();
}
